import { CheckCircle2, EyeOff, TrendingDown } from 'lucide-react';
import { buildRecommendations, type CatalogEntry } from './featureCatalog';

function Row({ entry, tone }: { entry: CatalogEntry; tone: 'unused' | 'unmeasured' }) {
  return (
    <li className="flex items-start gap-2.5 border-b border-zinc-100 py-2.5 last:border-0">
      {tone === 'unused' ? (
        <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" />
      ) : (
        <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300" />
      )}
      <div className="min-w-0">
        <p className="text-sm text-zinc-900">
          {entry.feature} <span className="text-xs text-zinc-400">· {entry.area}</span>
        </p>
        <p className="mt-0.5 text-xs text-zinc-500">
          {tone === 'unused'
            ? 'Tracked, but nothing fired in this range.'
            : entry.note || 'No instrumentation, so usage is unknown.'}
        </p>
      </div>
    </li>
  );
}

/**
 * What went unused — and, kept firmly separate, what we simply cannot see.
 *
 * The separation IS the feature. Most of this app emits no events, so a single "unused" list would
 * name a dozen well-used pages as dead and invite someone to retire one on that basis. The two
 * groups carry opposite recommendations: the first is evidence, the second is an absence of it.
 */
export function RecommendationsSection({ observedEvents }: { observedEvents: string[] }) {
  const recommendations = buildRecommendations(observedEvents);
  const unused = recommendations.filter((r) => r.kind === 'unused').map((r) => r.entry);
  const unmeasured = recommendations.filter((r) => r.kind === 'unmeasured').map((r) => r.entry);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-zinc-900">Went unused</h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          Instrumented features that fired zero events in this range. These are real candidates for
          simplifying or retiring — the evidence is that nobody reached for them.
        </p>
        <div className="mt-3">
          {unused.length === 0 ? (
            <p className="flex items-center gap-2 py-4 text-sm text-zinc-500">
              <CheckCircle2 className="h-4 w-4 text-zinc-400" />
              Every tracked feature was used at least once.
            </p>
          ) : (
            <ul>
              {unused.map((entry) => (
                <Row key={entry.feature} entry={entry} tone="unused" />
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-zinc-900">Cannot tell yet</h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          No instrumentation, so silence here means nothing. Do{' '}
          <strong className="font-medium text-zinc-600">not</strong> read these as unused — the next
          step is to measure them, not to cut them.
        </p>
        <div className="mt-3">
          {unmeasured.length === 0 ? (
            <p className="py-4 text-sm text-zinc-500">Everything in the catalogue is instrumented.</p>
          ) : (
            <ul>
              {unmeasured.map((entry) => (
                <Row key={entry.feature} entry={entry} tone="unmeasured" />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
